import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SPAWN } from "./world";

const VRCtx = createContext(null);
export const useVR = () => useContext(VRCtx);

export const VRProvider = ({ device = "desktop", children }) => {
  const [world, setWorld] = useState({ rooms: [], scientists: [] });
  const [catalog, setCatalog] = useState([]);
  const [room, setRoomState] = useState("atrium");
  const [quality, setQuality] = useState(() => localStorage.getItem("cgl_vr_q") || "auto");
  const [audioOn, setAudioOn] = useState(false);
  const [navMode, setNavMode] = useState("walk"); // walk | teleport
  const [reducedMotion, setReduced] = useState(false);

  // active overlays
  const [inspect, setInspect] = useState(null);     // product slug
  const [scientist, setScientist] = useState(null); // scientist key
  const [showMap, setShowMap] = useState(false);
  const [showCOA, setShowCOA] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showMolecule, setShowMolecule] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  // shared control input (mutated without re-render)
  const input = useRef({ keys: {}, move: [0, 0], dragging: false, teleport: null, spawnYaw: SPAWN.atrium.yaw });

  const sessionId = useRef(null);

  useEffect(() => {
    api.get("/vr/world").then(({ data }) => setWorld(data)).catch(() => {});
    api.get("/vr/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    api.post("/vr/session", { device, mode: "immersive" })
      .then(({ data }) => { sessionId.current = data.session_id; }).catch(() => {});
  }, [device]);

  useEffect(() => { localStorage.setItem("cgl_vr_q", quality); }, [quality]);

  const track = useCallback((event, extra = {}) => {
    api.post("/vr/analytics", { session_id: sessionId.current, event, device, ...extra }).catch(() => {});
  }, [device]);

  const setRoom = useCallback((key) => {
    setRoomState(key);
    setShowMap(false);
    const sp = SPAWN[key] || SPAWN.atrium;
    input.current.spawnYaw = sp.yaw;
    input.current.teleport = { pos: sp.pos, immediate: true };
    track("room_enter", { room: key });
  }, [track]);

  return (
    <VRCtx.Provider value={{
      world, catalog, device,
      room, setRoom,
      quality, setQuality, audioOn, setAudioOn, navMode, setNavMode, reducedMotion, setReduced,
      inspect, setInspect, scientist, setScientist,
      showMap, setShowMap, showCOA, setShowCOA, showSettings, setShowSettings, showCart, setShowCart,
      showMolecule, setShowMolecule, showTimeline, setShowTimeline,
      input, sessionId, track,
    }}>
      {children}
    </VRCtx.Provider>
  );
};
